import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { clsx } from "clsx";

import { useStateWithCallback } from "~/hooks/useStateWithCallback";
import { usePrevious } from "~/hooks/usePrevious";

import { TabList } from "./TabList";
import { TabPanel } from "./TabPanel";
import { Tab } from "./Tab";
import { TabLink } from "./TabLink";

import "./Tabs.css";

import type { CommonProps } from "../components";
import type { TabListProps } from "./TabList";
import type { TabPanelProps } from "./TabPanel";
import type { TabProps } from "./Tab";

type TabsChild =
  | React.ReactElement<typeof TabList>
  | React.ReactElement<typeof TabPanel>;

export interface TabsProps extends CommonProps {
  children?: TabsChild | TabsChild[];
  selected?: number;
  onSelectedChanged?: ({ selected }: { selected: number }) => void;
}

export function Tabs({
  children,
  className,
  selected: controlledSelected,
  onSelectedChanged,
  ...rest
}: TabsProps) {
  const tabNodesRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  const [_selectedIndex, setSelectedIndex] = useStateWithCallback(
    controlledSelected ?? 0,
    (selected: number) => {
      // Invoke the change handler
      onSelectedChanged?.({ selected });
    },
  );

  const selectedIndex =
    controlledSelected !== undefined ? controlledSelected : _selectedIndex;

  const prevSelectedIndex = usePrevious(selectedIndex);

  const handleClick = useCallback(
    (e: React.SyntheticEvent) => {
      const node = e.target as HTMLElement;
      const tab = node.closest("[aria-controls]");
      const attr = tab?.getAttribute("aria-controls");

      if (node && attr) {
        setSelectedIndex(Number(attr.split("-")[1]));
      }
    },
    [setSelectedIndex],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
          setSelectedIndex((v: number) => {
            // If at the end, go to the start
            if (v === tabNodesRef.current.size - 1) {
              return 0;
            }

            // Otherwise, go to the next tab
            return v + 1;
          });
          break;
        case "ArrowLeft":
          setSelectedIndex((v: number) => {
            // If at the start, go to the end
            if (v === 0) {
              return tabNodesRef.current.size - 1;
            }

            // Otherwise, go to the previous tab
            return v - 1;
          });
          break;
        case "Home":
          setSelectedIndex(0);
          break;
        case "End":
          setSelectedIndex(tabNodesRef.current.size - 1);
          break;
        default:
          break;
      }
    },
    [setSelectedIndex],
  );

  const getChildren = useCallback(() => {
    let tabsIndex = 0;
    let panelsIndex = 0;

    return Children.map(children, (x) => {
      // Render Tabs
      if (isValidElement<TabListProps>(x) && x.type === TabList) {
        return cloneElement(x, {
          onClick: handleClick,
          onKeyDown: handleKeyDown,
          children: Children.map(x.props.children, (tab) => {
            if (
              isValidElement<TabProps>(tab) &&
              (tab.type === Tab || tab.type === TabLink)
            ) {
              const isSelected = tabsIndex === selectedIndex;

              const id = `tab-${tabsIndex}`;

              const props = {
                ...tab.props,
                id,
                tabIndex: isSelected ? 0 : -1,
                "aria-selected": isSelected,
                "aria-controls": `tabpanel-${tabsIndex}`,
                ref: (node: HTMLButtonElement) => {
                  if (node) {
                    tabNodesRef.current = new Map(
                      tabNodesRef.current.set(id, node),
                    );
                  }
                },
              };

              tabsIndex += 1;

              return cloneElement(tab, props);
            }
          }),
        });
      }

      // Render TabPanels
      if (isValidElement<TabPanelProps>(x) && x.type === TabPanel) {
        const props = {
          ...x.props,
          id: `tabpanel-${panelsIndex}`,
          "aria-labelledby": `tab-${panelsIndex}`,
          selected: panelsIndex === selectedIndex,
        };

        panelsIndex += 1;

        return cloneElement(x, props);
      }
    });
  }, [children, selectedIndex, handleClick, handleKeyDown]);

  useEffect(() => {
    if (
      prevSelectedIndex === undefined ||
      prevSelectedIndex === selectedIndex
    ) {
      return;
    }
    // Ensure the tab node is focused. This is necessary
    // to accomodate keyboard navigation.
    tabNodesRef.current.get(`tab-${selectedIndex}`)?.focus();
  }, [prevSelectedIndex, selectedIndex]);

  return (
    <div className={clsx("Tabs", className)} {...rest}>
      {getChildren()}
    </div>
  );
}

export { TabList } from "./TabList";
export { Tab } from "./Tab";
export { TabPanel } from "./TabPanel";
export { TabNavigation } from "./TabNavigation";
export { TabLink } from "./TabLink";
export { TabOutlet } from "./TabOutlet";
